import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { AuthService } from '../../services/auth.service';
import { deriveKeys, encryptVaultKey, encryptKeyData } from '../../utils/encryption';
import { store } from '../../store';
import Input from '../common/Input';
import Button from '../common/Button';
import PasswordStrengthMeter from '../common/PasswordStrengthMeter';
import { validatePassword, validatePasswordMatch } from '../../utils/validation';
import { generateStrongPassword } from '../../utils/passwordGenerator';

interface AddProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

enum AddProfileStep {
    EMAIL_VERIFICATION,
    CODE_VERIFICATION,
    MASTER_PASSWORD
}

const AddProfileModal: React.FC<AddProfileModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const [step, setStep] = useState<AddProfileStep>(AddProfileStep.EMAIL_VERIFICATION);
    const [email, setEmail] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [masterPassword, setMasterPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordHint, setPasswordHint] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const userId = localStorage.getItem('userId') || store.getState().auth.user?.id;

    const resetForm = () => {
        setStep(AddProfileStep.EMAIL_VERIFICATION);
        setEmail('');
        setVerificationCode('');
        setMasterPassword('');
        setConfirmPassword('');
        setPasswordHint('');
        setError('');
        setValidationErrors({});
    };

    const handleSendVerification = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) {
            setError('Email is required');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const result = await AuthService.initiateProfileLink(email);
            if (result.success) {
                toast.success('Verification code sent to your email');
                setStep(AddProfileStep.CODE_VERIFICATION);
            } else {
                setError(result.message || 'Failed to send verification code');
            }
        } catch (err) {
            setError('An error occurred while sending the verification code');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!verificationCode) {
            setError('Verification code is required');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const result = await AuthService.verifyProfileEmail(email, verificationCode);
            if (result.success) {
                toast.success('Email verified successfully');
                setStep(AddProfileStep.MASTER_PASSWORD);
            } else {
                setError(result.message || 'Invalid verification code');
            }
        } catch (err) {
            setError('An error occurred while verifying the code');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!validatePassword(masterPassword)) {
            errors.password = 'Password must be at least 8 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character';
        }

        if (!validatePasswordMatch(masterPassword, confirmPassword)) {
            errors.confirmPassword = 'Passwords do not match';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleGeneratePassword = () => {
        const newPassword = generateStrongPassword();
        setMasterPassword(newPassword);
        setConfirmPassword(newPassword);
    };

    const handleCreateProfile = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form
        const isValid = validateForm();
        if (!isValid) return;

        setIsLoading(true);
        setError('');

        try {
            if (!userId) {
                setError('User ID not found. Please try logging in again.');
                setIsLoading(false);
                return;
            }

            // Derive keys on client side
            const { authKey, encryptionKey, symmetricKey } = deriveKeys(
                masterPassword,
                email
            );

            // Encrypt symmetric key with encryption key
            const encryptedVaultKey = encryptVaultKey(symmetricKey, encryptionKey);
            const encryptedKey = encryptKeyData(encryptionKey);

            // Register linked profile
            const registerResult = await AuthService.registerLinkedProfile(
                email,
                authKey,
                encryptedVaultKey,
                encryptedKey,
                passwordHint.length > 0 ? passwordHint : null,
                userId,
            );

            if (registerResult.success) {
                toast.success('Profile added successfully');
                resetForm();
                onSuccess();
                onClose();
            } else {
                setError(registerResult.data?.message || 'Failed to create profile');
            }
        } catch (err) {
            setError('An error occurred while creating the profile');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const renderStepContent = () => {
        switch (step) {
            case AddProfileStep.EMAIL_VERIFICATION:
                return (
                    <form onSubmit={handleSendVerification}>
                        <Input
                            label="Email Address"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter email address"
                            required
                        />

                        <div className="mt-6 flex justify-end">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={onClose}
                                className="mr-2"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={isLoading}
                            >
                                Send Verification Code
                            </Button>
                        </div>
                    </form>
                );

            case AddProfileStep.CODE_VERIFICATION:
                return (
                    <form onSubmit={handleVerifyCode}>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            We've sent a verification code to <strong>{email}</strong>
                        </p>

                        <Input
                            label="Verification Code"
                            type="text"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            placeholder="Enter verification code"
                            required
                        />

                        <div className="mt-6 flex justify-between">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => setStep(AddProfileStep.EMAIL_VERIFICATION)}
                            >
                                Back
                            </Button>
                            <Button
                                type="submit"
                                loading={isLoading}
                            >
                                Verify Code
                            </Button>
                        </div>
                    </form>
                );

            case AddProfileStep.MASTER_PASSWORD:
                return (
                    <form onSubmit={handleCreateProfile}>
                        <div className="mb-4">
                            <div className="flex justify-between items-center">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Master Password
                                </label>
                                <button
                                    type="button"
                                    onClick={handleGeneratePassword}
                                    className="text-sm text-blue-600 hover:text-blue-500"
                                >
                                    Generate a Strong Password
                                </button>
                            </div>
                            <Input
                                type="password"
                                value={masterPassword}
                                onChange={(e) => setMasterPassword(e.target.value)}
                                placeholder="Create a strong master password"
                                required
                                showPasswordToggle
                                error={validationErrors.password}
                            />

                            {masterPassword && (
                                <PasswordStrengthMeter
                                    password={masterPassword}
                                    email={email}
                                />
                            )}
                        </div>

                        <div className="mb-4">
                            <Input
                                label="Confirm Master Password"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm your master password"
                                required
                                showPasswordToggle
                                error={validationErrors.confirmPassword}
                            />
                        </div>
                        <div className="mb-4">
                            <label htmlFor="masterPasswordHint" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Master Password Hint (Optional)
                            </label>
                            <Input
                                id="masterPasswordHint"
                                type="text"
                                value={passwordHint}
                                onChange={(e) => setPasswordHint(e.target.value)}
                                placeholder="Enter a hint to help remember your password"
                            />
                        </div>

                        <div className="mt-6 flex justify-between">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => setStep(AddProfileStep.CODE_VERIFICATION)}
                            >
                                Back
                            </Button>
                            <Button
                                type="submit"
                                loading={isLoading}
                            >
                                Create Profile
                            </Button>
                        </div>
                    </form>
                );
            default:
                return null;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
                </div>

                <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className="absolute top-0 right-0 pt-4 pr-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                        >
                            <span className="sr-only">Close</span>
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                        <div className="mt-3 text-center sm:mt-0 sm:text-left">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                                {step === AddProfileStep.EMAIL_VERIFICATION && 'Add Another Account'}
                                {step === AddProfileStep.CODE_VERIFICATION && 'Verify Email'}
                                {step === AddProfileStep.MASTER_PASSWORD && 'Create Master Password'}
                            </h3>

                            {error && (
                                <div className="mt-2 p-2 bg-red-100 dark:bg-red-900 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-200 rounded">
                                    {error}
                                </div>
                            )}

                            <div className="mt-4">
                                {renderStepContent()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddProfileModal; 